import Character3D from '@/components/game/elements/Character3D';
import { Modal, PixelIcon, type ModalTab } from '@/components/ui';
import { useShop } from '@/contexts/ShopContext';
import { PROPS_CATALOG, type PropSlot } from '@/types/props';
import { COLOR } from '@/config/designTokens';
import { AnimatePresence, LayoutGroup, motion } from 'motion/react';
import { useCallback, useState } from 'react';

interface CharacterCustomizationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface CharacterConfig {
  id: string;
  name: string;
  description: string;
  abilities: string[];
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  config: any;
}

interface CharacterAbility {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
}

const CHARACTER_ABILITIES: Record<string, CharacterAbility> = {
  speed: {
    id: 'speed',
    name: 'Speed Boost',
    description: 'Move 25% faster',
    icon: 'lightning',
    color: '#FFD700',
  },
  shield: {
    id: 'shield',
    name: 'Shield',
    description: 'Immune to first obstacle hit',
    icon: 'shield',
    color: '#4CAF50',
  },
  magnet: {
    id: 'magnet',
    name: 'Coin Magnet',
    description: 'Attract coins from distance',
    icon: 'coin',
    color: '#FF9800',
  },
  double_jump: {
    id: 'double_jump',
    name: 'Double Jump',
    description: 'Jump over obstacles',
    icon: 'arrow-up',
    color: '#2196F3',
  },
};

const CHARACTER_CONFIGS: CharacterConfig[] = [
  {
    id: 'default_hero',
    name: 'Classic Hero',
    description: 'The legendary red cube that started it all',
    abilities: ['speed'],
    rarity: 'common',
    config: {},
  },
  {
    id: 'tiny_blue',
    name: 'Velocity',
    description: 'Small, fast, and precise',
    abilities: ['speed', 'double_jump'],
    rarity: 'rare',
    config: {
      sizes: { characterSize: 0.7, cubeSize: 0.95 },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #4a90e2 0%, #357abd 50%, #2968a3 100%)',
          back: 'linear-gradient(135deg, #357abd 0%, #2968a3 50%, #1e5189 100%)',
          top: 'linear-gradient(135deg, #5ba0f2 0%, #4a90e2 50%, #357abd 100%)',
          bottom: 'linear-gradient(135deg, #2968a3 0%, #1e5189 50%, #143a5f 100%)',
          left: 'linear-gradient(135deg, #4080d0 0%, #3570c0 50%, #2a60b0 100%)',
          right: 'linear-gradient(135deg, #4585d5 0%, #3a75c5 50%, #2f65b5 100%)',
        },
      },
      face: { eyes: { size: '35%' } },
    },
  },
  {
    id: 'chonky_green',
    name: 'Tank',
    description: 'Big, tough, and resilient',
    abilities: ['shield', 'magnet'],
    rarity: 'epic',
    config: {
      sizes: { characterSize: 1.2, cubeSize: 1.1 },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #4caf50 0%, #45a049 50%, #388e3c 100%)',
          back: 'linear-gradient(135deg, #388e3c 0%, #2e7d32 50%, #1b5e20 100%)',
          top: 'linear-gradient(135deg, #66bb6a 0%, #4caf50 50%, #43a047 100%)',
          bottom: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 50%, #0d3f15 100%)',
          left: 'linear-gradient(135deg, #43a047 0%, #3b9040 50%, #338037 100%)',
          right: 'linear-gradient(135deg, #48a84c 0%, #409843 50%, #38883b 100%)',
        },
      },
      face: { eyes: { size: '15%', gap: '35%' }, mouth: { width: '40%' } },
    },
  },
  {
    id: 'sleek_purple',
    name: 'Phantom',
    description: 'Elegant and mysterious',
    abilities: ['double_jump', 'speed'],
    rarity: 'epic',
    config: {
      sizes: { characterSize: 0.75, cubeSize: 0.98 },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #9c27b0 0%, #8e24aa 50%, #7b1fa2 100%)',
          back: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 50%, #4a148c 100%)',
          top: 'linear-gradient(135deg, #ab47bc 0%, #9c27b0 50%, #8e24aa 100%)',
          bottom: 'linear-gradient(135deg, #6a1b9a 0%, #4a148c 50%, #2a0a5c 100%)',
          left: 'linear-gradient(135deg, #8e24aa 0%, #7e22a0 50%, #6e1f96 100%)',
          right: 'linear-gradient(135deg, #9125ac 0%, #8123a2 50%, #712098 100%)',
        },
        effects: {
          shadow: 'radial-gradient(ellipse, rgba(156, 39, 176, 0.4) 0%, transparent 70%)',
        },
      },
      face: {
        eyes: { size: '22%', shine: { size: '40%', position: { top: '15%', left: '20%' } } },
        mouth: { width: '30%', heights: { neutral: '12%', happy: '20%', shocked: '25%' } },
      },
    },
  },
  {
    id: 'golden_glow',
    name: 'Legend',
    description: 'Radiant and prestigious with all abilities!',
    abilities: ['speed', 'shield', 'magnet', 'double_jump'],
    rarity: 'legendary',
    config: {
      sizes: { characterSize: 0.8, cubeSize: 0.9 },
      colors: {
        faces: {
          front: 'linear-gradient(135deg, #ffc107 0%, #ffb300 50%, #ffa000 100%)',
          back: 'linear-gradient(135deg, #ffa000 0%, #ff8f00 50%, #ff6f00 100%)',
          top: 'linear-gradient(135deg, #ffca28 0%, #ffc107 50%, #ffb300 100%)',
          bottom: 'linear-gradient(135deg, #ff8f00 0%, #ff6f00 50%, #e65100 100%)',
          left: 'linear-gradient(135deg, #ffb300 0%, #ffa300 50%, #ff9300 100%)',
          right: 'linear-gradient(135deg, #ffb800 0%, #ffa800 50%, #ff9800 100%)',
        },
      },
      backFace: { symbol: '*', sizeScale: 0.4, opacity: 0.6 },
    },
  },
];

const CharacterCustomizationModal: React.FC<CharacterCustomizationModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { shopState, equipCharacter, equipProp, unequipProp } = useShop();

  const playSound = (sound: string) => {};

  const [activeTab, setActiveTab] = useState<'characters' | 'props' | 'abilities'>('characters');
  const [selectedSlot, setSelectedSlot] = useState<PropSlot | 'all'>('all');

  // Get rarity color
  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common':
        return '#9E9E9E';
      case 'rare':
        return '#2196F3';
      case 'epic':
        return '#9C27B0';
      case 'legendary':
        return '#FF9800';
      default:
        return '#9E9E9E';
    }
  };

  // Get rarity glow
  const getRarityGlow = (rarity: string) => {
    const colors = {
      common: 'rgba(158, 158, 158, 0.3)',
      rare: 'rgba(33, 150, 243, 0.4)',
      epic: 'rgba(156, 39, 176, 0.5)',
      legendary: 'rgba(255, 152, 0, 0.6)',
    };
    return colors[rarity as keyof typeof colors] || colors.common;
  };

  // Get available characters (owned only)
  const availableCharacters = CHARACTER_CONFIGS.filter(
    char => char.id === 'default_hero' || shopState.ownedCharacters.includes(char.id)
  );

  // Get available props (owned only)
  const getPropsForSlot = (slot: PropSlot | 'all') => {
    const ownedProps = PROPS_CATALOG.filter(prop => shopState.ownedProps.includes(prop.id));
    if (slot === 'all') return ownedProps;
    return ownedProps.filter(prop => prop.slot === slot);
  };

  // Current character config
  const currentCharacter =
    CHARACTER_CONFIGS.find(char => char.id === shopState.equippedCharacter) || CHARACTER_CONFIGS[0];

  // Current equipped props as objects
  const equippedProps = shopState.equippedProps
    .map(id => PROPS_CATALOG.find(p => p.id === id)!)
    .filter(Boolean);

  // Prop slots with counts
  const propSlots: Array<{ id: PropSlot | 'all'; label: string; icon: string; count: number }> = [
    { id: 'all', label: 'All', icon: 'mask', count: getPropsForSlot('all').length },
    { id: 'head', label: 'Head', icon: 'crown', count: getPropsForSlot('head').length },
    { id: 'face', label: 'Face', icon: 'glasses', count: getPropsForSlot('face').length },
    { id: 'hair', label: 'Hair', icon: 'hair', count: getPropsForSlot('hair').length },
    { id: 'hand', label: 'Hand', icon: 'hand', count: getPropsForSlot('hand').length },
    { id: 'back', label: 'Back', icon: 'backpack', count: getPropsForSlot('back').length },
    {
      id: 'accessory',
      label: 'Accessory',
      icon: 'gem',
      count: getPropsForSlot('accessory').length,
    },
  ];

  const handleCharacterSelect = useCallback(
    (characterId: string) => {
      playSound('select');
      equipCharacter(characterId);
    },
    [equipCharacter, playSound]
  );

  const handlePropToggle = useCallback(
    (propId: string) => {
      if (shopState.equippedProps.includes(propId)) {
        playSound('unequip');
        unequipProp(propId);
      } else {
        playSound('equip');
        equipProp(propId);
      }
    },
    [shopState.equippedProps, equipProp, unequipProp, playSound]
  );

  const tabs: ModalTab[] = [
    {
      id: 'characters',
      label: 'Characters',
      icon: 'user',
      count: availableCharacters.length,
    },
    {
      id: 'props',
      label: 'Props',
      icon: 'settings',
      count: shopState.ownedProps.length,
    },
    {
      id: 'abilities',
      label: 'Abilities',
      icon: 'lightning',
      count: currentCharacter.abilities?.length || 0,
    },
  ];

  const headerContent = (
    <motion.div
      className="customization-stats"
      style={{
        display: 'flex',
        gap: '16px',
        alignItems: 'center',
        marginTop: '12px',
        fontSize: '11px',
      }}
      layout
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <PixelIcon name="user" size={16} color="#4caf50" />
        <span>{currentCharacter.name}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <PixelIcon name="settings" size={16} color="#2196f3" />
        <span>{equippedProps.length} equipped</span>
      </div>
    </motion.div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Character Customization"
      subtitle="Customize your character's appearance and equip props"
      size="large"
      variant="info"
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={tabId => setActiveTab(tabId as 'characters' | 'props' | 'abilities')}
      headerContent={headerContent}
    >
      <style>
        {`
          .customization-container {
            display: flex;
            flex-direction: column;
            gap: 16px;
            width: 100%;
            max-width: 100%;
          }
          
          .character-description {
            display: none;
          }
          
          .equipped-text {
            display: none;
          }
          
          @media (min-width: 768px) {
            .customization-container {
              flex-direction: row;
              gap: 24px;
              align-items: flex-start;
            }
            
            .character-preview-section {
              flex: 0 0 280px;
              position: sticky;
              top: 20px;
            }
            
            .customization-options {
              flex: 1;
              min-width: 0;
            }
            
            .character-description {
              display: block;
            }
            
            .equipped-text {
              display: inline;
            }
          }
          
          .character-card {
            transition: all 0.3s ease;
          }
          
          .character-card .character-card-inner > div {
            position: relative !important;
          }
          
          .slot-filter {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .slot-filter::-webkit-scrollbar {
            display: none;
          }
          
          .props-grid {
            scrollbar-width: thin;
          }
        `}
      </style>
      <LayoutGroup>
        <div className="customization-container">
          {/* Character Preview - Mobile First */}
          <motion.div 
            className="character-preview-section" 
            layout
          >
            <motion.div
              className="character-preview-container"
              style={{
                background: `linear-gradient(135deg, var(--pixel-bg) 0%, ${getRarityColor(currentCharacter.rarity)}20 100%)`,
                border: `3px solid ${getRarityColor(currentCharacter.rarity)}`,
                borderRadius: '20px',
                padding: '16px',
                textAlign: 'center',
                marginBottom: '16px',
                minHeight: '160px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                boxShadow: `0 8px 32px ${getRarityGlow(currentCharacter.rarity)}, inset 0 1px 0 rgba(255,255,255,0.1)`,
              }}
              whileHover={{ scale: 1.02, y: -2 }}
              layout
            >
              {/* Rarity Badge */}
              <motion.div
                style={{
                  position: 'absolute',
                  top: '12px',
                  right: '12px',
                  background: `linear-gradient(135deg, ${getRarityColor(currentCharacter.rarity)}, ${getRarityColor(currentCharacter.rarity)}CC)`,
                  color: 'white',
                  fontSize: '10px',
                  padding: '4px 8px',
                  borderRadius: '12px',
                  textTransform: 'uppercase',
                  fontWeight: 'bold',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.2)',
                }}
                initial={{ scale: 0, rotate: -45 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: 'spring', delay: 0.2 }}
              >
                {currentCharacter.rarity}
              </motion.div>
              
              <Character3D
                position={[0, 0]}
                direction={null}
                isMoving={false}
                cellSize={80}
                gameState="playing"
                config={currentCharacter.config}
                equippedProps={equippedProps}
              />
              <motion.div
                style={{ marginTop: '12px', width: '100%' }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="character-title" style={{ 
                  fontSize: '16px', 
                  margin: '0 0 4px 0',
                  color: getRarityColor(currentCharacter.rarity),
                  fontWeight: 'bold'
                }}>
                  {currentCharacter.name}
                </h3>
                <p className="character-desc" style={{ 
                  fontSize: '11px', 
                  margin: '0 0 12px 0', 
                  color: 'var(--pixel-text-secondary)',
                  lineHeight: '1.4'
                }}>
                  {currentCharacter.description}
                </p>
                
                {/* Abilities Quick Preview */}
                <motion.div 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '6px', 
                    flexWrap: 'wrap'
                  }}
                  layout
                >
                  {(currentCharacter.abilities || []).map((abilityId, i) => {
                    const ability = CHARACTER_ABILITIES[abilityId];
                    if (!ability) return null;
                    return (
                      <motion.div
                        key={abilityId}
                        style={{
                          background: `linear-gradient(135deg, ${ability.color}, ${ability.color}CC)`,
                          borderRadius: '50%',
                          width: window.innerWidth < 768 ? '20px' : '24px',
                          height: window.innerWidth < 768 ? '20px' : '24px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: `0 2px 8px ${ability.color}50`,
                          border: '1px solid rgba(255,255,255,0.2)',
                        }}
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ delay: 0.4 + i * 0.1, type: 'spring' }}
                        whileHover={{ scale: 1.2, rotate: 360 }}
                        title={ability.name}
                      >
                        <PixelIcon name={ability.icon} size={window.innerWidth < 768 ? 10 : 12} color="white" />
                      </motion.div>
                    );
                  })}
                </motion.div>
              </motion.div>
            </motion.div>

            {/* Equipped Props Summary - Compact for mobile */}
            <motion.div
              className="equipped-props-summary"
              style={{
                background: 'var(--pixel-bg-secondary)',
                border: '2px solid var(--pixel-border)',
                borderRadius: '16px',
                padding: window.innerWidth < 768 ? '12px' : '16px',
              }}
              layout
            >
              <h4
                style={{
                  fontSize: '12px',
                  margin: '0 0 12px 0',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                <PixelIcon name="settings" size={16} color="#4caf50" />
                Equipped Props
              </h4>
              <AnimatePresence mode="popLayout">
                {equippedProps.length === 0 ? (
                  <motion.p
                    style={{ fontSize: '10px', color: 'var(--pixel-text-secondary)', margin: 0 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    No props equipped
                  </motion.p>
                ) : (
                  <motion.div
                    style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                    layout
                  >
                    {equippedProps.map(prop => (
                      <motion.div
                        key={prop.id}
                        className="equipped-prop-item"
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px',
                          background: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '8px',
                          fontSize: '10px',
                        }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                        whileHover={{ scale: 1.02 }}
                        layout
                      >
                        <span style={{ fontSize: '16px' }}>{prop.model.icon}</span>
                        <span style={{ flex: 1 }}>{prop.name}</span>
                        <motion.button
                          className="unequip-btn"
                          onClick={() => unequipProp(prop.id)}
                          style={{
                            background: 'var(--pixel-danger)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '50%',
                            width: '20px',
                            height: '20px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            fontSize: '12px',
                          }}
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          ×
                        </motion.button>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>

          {/* Customization Options - Full width on mobile */}
          <motion.div 
            className="customization-options" 
            style={{ 
              width: '100%',
              marginTop: window.innerWidth < 768 ? '0' : '20px'
            }} 
            layout
          >
            <AnimatePresence mode="wait">
              {/* Characters Tab */}
              {activeTab === 'characters' && (
                <motion.div
                  key="characters"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className="characters-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth < 768 
                        ? 'repeat(auto-fit, minmax(140px, 1fr))' 
                        : 'repeat(auto-fit, minmax(180px, 1fr))',
                      gap: window.innerWidth < 768 ? '12px' : '16px',
                    }}
                    layout
                  >
                    {availableCharacters.map((character, index) => {
                      const isSelected = character.id === shopState.equippedCharacter;
                      return (
                        <motion.div
                          key={character.id}
                          className="character-card"
                          style={{
                            background: isSelected
                              ? `linear-gradient(135deg, ${getRarityColor(character.rarity)}20, ${getRarityColor(character.rarity)}10)`
                              : 'var(--pixel-bg-secondary)',
                            border: `2px solid ${getRarityColor(character.rarity)}`,
                            borderRadius: '16px',
                            padding: window.innerWidth < 768 ? '12px' : '16px',
                            cursor: 'pointer',
                            textAlign: 'center',
                            boxShadow: isSelected
                              ? `0 8px 32px ${getRarityGlow(character.rarity)}, 0 0 0 2px ${getRarityColor(character.rarity)}40`
                              : `0 4px 16px ${getRarityGlow(character.rarity)}`,
                            position: 'relative',
                            overflow: 'hidden',
                            backdropFilter: 'blur(10px)',
                          }}
                          whileHover={{
                            scale: 1.05,
                            y: -4,
                            boxShadow: `0 12px 40px ${getRarityGlow(character.rarity)}, 0 0 0 2px ${getRarityColor(character.rarity)}60`,
                          }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleCharacterSelect(character.id)}
                          initial={{ opacity: 0, y: 20, scale: 0.9 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          transition={{ delay: index * 0.1, type: 'spring' }}
                          layout
                        >
                          <div
                            className="character-card-inner"
                            style={{
                              height: '60px',
                              marginBottom: '8px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              position: 'relative'
                            }}
                          >
                            <Character3D
                              position={[0, 0]}
                              direction={null}
                              isMoving={false}
                              cellSize={40}
                              gameState="playing"
                              config={character.config}
                            />
                          </div>
                          {/* Rarity Badge */}
                          <motion.div
                            style={{
                              position: 'absolute',
                              top: '6px',
                              right: '6px',
                              background: `linear-gradient(135deg, ${getRarityColor(character.rarity)}, ${getRarityColor(character.rarity)}CC)`,
                              color: 'white',
                              fontSize: window.innerWidth < 768 ? '7px' : '8px',
                              padding: window.innerWidth < 768 ? '2px 4px' : '3px 6px',
                              borderRadius: '8px',
                              textTransform: 'uppercase',
                              fontWeight: 'bold',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(255,255,255,0.2)',
                            }}
                            initial={{ scale: 0, rotate: -45 }}
                            animate={{ scale: 1, rotate: 0 }}
                            transition={{ delay: index * 0.1 + 0.2, type: 'spring' }}
                          >
                            {character.rarity}
                          </motion.div>

                          <h4 style={{ 
                            fontSize: window.innerWidth < 768 ? '10px' : '12px', 
                            margin: '0 0 2px 0',
                            color: getRarityColor(character.rarity),
                            fontWeight: 'bold'
                          }}>
                            {character.name}
                          </h4>
                          {window.innerWidth >= 768 && (
                            <p
                              style={{
                                fontSize: '8px',
                                margin: '0 0 6px 0',
                                color: 'var(--pixel-text-secondary)',
                                lineHeight: '1.2',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                              }}
                            >
                              {character.description}
                            </p>
                          )}

                          {/* Abilities Preview - Compact */}
                          <motion.div
                            style={{
                              display: 'flex',
                              justifyContent: 'center',
                              gap: window.innerWidth < 768 ? '2px' : '4px',
                              marginBottom: '6px',
                              flexWrap: 'wrap',
                              minHeight: '18px',
                            }}
                            layout
                          >
                            {character.abilities?.slice(0, window.innerWidth < 768 ? 2 : 3).map((abilityId, i) => {
                              const ability = CHARACTER_ABILITIES[abilityId];
                              if (!ability) return null;
                              return (
                                <motion.div
                                  key={abilityId}
                                  style={{
                                    background: `linear-gradient(135deg, ${ability.color}, ${ability.color}CC)`,
                                    borderRadius: '50%',
                                    width: window.innerWidth < 768 ? '12px' : '16px',
                                    height: window.innerWidth < 768 ? '12px' : '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    border: '1px solid rgba(255,255,255,0.3)',
                                    boxShadow: `0 2px 4px ${ability.color}40`,
                                  }}
                                  initial={{ scale: 0, rotate: -180 }}
                                  animate={{ scale: 1, rotate: 0 }}
                                  transition={{
                                    delay: index * 0.1 + i * 0.1 + 0.3,
                                    type: 'spring',
                                  }}
                                  title={ability.name}
                                >
                                  <PixelIcon name={ability.icon} size={window.innerWidth < 768 ? 6 : 8} color="white" />
                                </motion.div>
                              );
                            })}
                            {character.abilities && character.abilities.length > (window.innerWidth < 768 ? 2 : 3) && (
                              <motion.div
                                style={{
                                  background: 'rgba(255, 255, 255, 0.3)',
                                  borderRadius: '50%',
                                  width: window.innerWidth < 768 ? '12px' : '16px',
                                  height: window.innerWidth < 768 ? '12px' : '16px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontSize: window.innerWidth < 768 ? '6px' : '8px',
                                  color: 'white',
                                  fontWeight: 'bold',
                                  border: '1px solid rgba(255,255,255,0.3)',
                                }}
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: index * 0.1 + 0.6, type: 'spring' }}
                              >
                                +{character.abilities.length - (window.innerWidth < 768 ? 2 : 3)}
                              </motion.div>
                            )}
                          </motion.div>
                          {isSelected && (
                            <motion.div
                              style={{
                                marginTop: '4px',
                                fontSize: window.innerWidth < 768 ? '8px' : '10px',
                                color: 'white',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '3px',
                                background: `linear-gradient(135deg, ${getRarityColor(character.rarity)}, ${getRarityColor(character.rarity)}AA)`,
                                padding: '2px 6px',
                                borderRadius: '8px',
                                fontWeight: 'bold',
                              }}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ type: 'spring' }}
                            >
                              <PixelIcon name="success" size={window.innerWidth < 768 ? 8 : 10} color="white" />
                              {window.innerWidth >= 768 ? 'Equipped' : 'OK'}
                            </motion.div>
                          )}
                        </motion.div>
                      );
                    })}
                  </motion.div>
                </motion.div>
              )}

              {/* Abilities Tab */}
              {activeTab === 'abilities' && (
                <motion.div
                  key="abilities"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    style={{
                      textAlign: 'center',
                      marginBottom: '24px',
                      padding: '20px',
                      background: 'var(--pixel-bg-secondary)',
                      borderRadius: '12px',
                      border: `2px solid ${getRarityColor(currentCharacter.rarity)}`,
                      boxShadow: `0 0 15px ${getRarityGlow(currentCharacter.rarity)}`,
                    }}
                    layout
                  >
                    <h3
                      style={{
                        fontSize: '14px',
                        margin: '0 0 8px 0',
                        color: getRarityColor(currentCharacter.rarity),
                      }}
                    >
                      Character Abilities
                    </h3>
                    <p
                      style={{
                        fontSize: '10px',
                        margin: '0 0 16px 0',
                        color: 'var(--pixel-text-secondary)',
                      }}
                    >
                      Your {currentCharacter.name} has {currentCharacter.abilities?.length || 0}{' '}
                      unique abilities
                    </p>

                    <motion.div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: window.innerWidth < 768 
                          ? 'repeat(auto-fit, minmax(140px, 1fr))'
                          : 'repeat(auto-fit, minmax(160px, 1fr))',
                        gap: window.innerWidth < 768 ? '8px' : '12px',
                        marginTop: '16px',
                      }}
                      layout
                    >
                      {(currentCharacter.abilities || []).map((abilityId, index) => {
                        const ability = CHARACTER_ABILITIES[abilityId];
                        if (!ability) return null;

                        return (
                          <motion.div
                            key={abilityId}
                            style={{
                              background: `linear-gradient(135deg, ${ability.color}20, ${ability.color}10)`,
                              border: `2px solid ${ability.color}`,
                              borderRadius: '10px',
                              padding: '16px',
                              textAlign: 'center',
                            }}
                            initial={{ opacity: 0, scale: 0.8, rotateY: -90 }}
                            animate={{ opacity: 1, scale: 1, rotateY: 0 }}
                            transition={{ delay: index * 0.1, type: 'spring', stiffness: 200 }}
                            whileHover={{ scale: 1.05, y: -2 }}
                            layout
                          >
                            <motion.div
                              style={{
                                width: '40px',
                                height: '40px',
                                background: ability.color,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto 12px auto',
                                boxShadow: `0 0 15px ${ability.color}50`,
                              }}
                              whileHover={{ rotate: 360, scale: 1.1 }}
                              transition={{ duration: 0.6 }}
                            >
                              <PixelIcon name={ability.icon} size={20} color="white" />
                            </motion.div>
                            <h4
                              style={{
                                fontSize: '11px',
                                margin: '0 0 4px 0',
                                color: ability.color,
                              }}
                            >
                              {ability.name}
                            </h4>
                            <p
                              style={{
                                fontSize: '9px',
                                margin: 0,
                                color: 'var(--pixel-text-secondary)',
                              }}
                            >
                              {ability.description}
                            </p>
                          </motion.div>
                        );
                      })}
                    </motion.div>

                    {(!currentCharacter.abilities || currentCharacter.abilities.length === 0) && (
                      <motion.div
                        style={{
                          textAlign: 'center',
                          padding: '40px',
                          color: 'var(--pixel-text-secondary)',
                        }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                      >
                        <PixelIcon name="lightning" size={32} color="var(--pixel-text-secondary)" />
                        <p style={{ marginTop: '12px', fontSize: '12px' }}>No special abilities</p>
                        <p style={{ fontSize: '10px', margin: '4px 0 0 0' }}>
                          Unlock new characters with unique abilities in the shop!
                        </p>
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
              )}

              {/* Props Tab */}
              {activeTab === 'props' && (
                <motion.div
                  key="props"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Slot Filter - Responsive */}
                  <motion.div
                    className="slot-filter"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth < 768 
                        ? 'repeat(auto-fit, minmax(60px, 1fr))'
                        : 'repeat(auto-fit, minmax(80px, 1fr))',
                      gap: window.innerWidth < 768 ? '4px' : '8px',
                      marginBottom: '16px',
                    }}
                    layout
                  >
                    {propSlots.map(slot => (
                      <motion.button
                        key={slot.id}
                        className={`slot-btn ${selectedSlot === slot.id ? 'active' : ''}`}
                        onClick={() => setSelectedSlot(slot.id)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: window.innerWidth < 768 ? '2px' : '4px',
                          padding: window.innerWidth < 768 ? '6px 4px' : '8px 8px',
                          background:
                            selectedSlot === slot.id
                              ? 'var(--pixel-primary)'
                              : 'rgba(255, 255, 255, 0.1)',
                          border: `2px solid ${
                            selectedSlot === slot.id ? 'var(--pixel-primary)' : 'transparent'
                          }`,
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontSize: window.innerWidth < 768 ? '8px' : '9px',
                          color: 'white',
                          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
                          textAlign: 'center',
                          minHeight: window.innerWidth < 768 ? '40px' : '32px',
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        layout
                      >
                        <PixelIcon name={slot.icon} size={16} />
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {window.innerWidth < 768 ? slot.label.slice(0, 3) : slot.label}
                          {window.innerWidth >= 768 && ` (${slot.count})`}
                        </span>
                        {window.innerWidth < 768 && (
                          <span style={{ fontSize: '6px', opacity: 0.8 }}>({slot.count})</span>
                        )}
                      </motion.button>
                    ))}
                  </motion.div>

                  {/* Props Grid - Mobile responsive */}
                  <motion.div
                    className="props-grid"
                    style={{
                      display: 'grid',
                      gridTemplateColumns: window.innerWidth < 768 
                        ? 'repeat(auto-fit, minmax(100px, 1fr))'
                        : 'repeat(auto-fit, minmax(120px, 1fr))',
                      gap: window.innerWidth < 768 ? '8px' : '12px',
                      maxHeight: window.innerWidth < 768 ? '280px' : '320px',
                      overflowY: 'auto',
                      padding: '4px',
                    }}
                    layout
                  >
                    <AnimatePresence mode="popLayout">
                      {getPropsForSlot(selectedSlot).length === 0 ? (
                        <motion.div
                          style={{
                            gridColumn: '1 / -1',
                            textAlign: 'center',
                            padding: '40px',
                            color: 'var(--pixel-text-secondary)',
                          }}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          <PixelIcon name="mask" size={32} color="var(--pixel-text-secondary)" />
                          <p style={{ marginTop: '12px', fontSize: '12px' }}>No props available</p>
                          <p style={{ fontSize: '10px', margin: '4px 0 0 0' }}>
                            Visit the shop to buy more!
                          </p>
                        </motion.div>
                      ) : (
                        getPropsForSlot(selectedSlot).map((prop, index) => {
                          const isEquipped = shopState.equippedProps.includes(prop.id);
                          return (
                            <motion.div
                              key={prop.id}
                              className="prop-card"
                              style={{
                                background: isEquipped
                                  ? 'var(--pixel-success)'
                                  : 'var(--pixel-bg-secondary)',
                                border: `2px solid ${
                                  isEquipped ? 'var(--pixel-success)' : 'var(--pixel-border)'
                                }`,
                                borderRadius: '10px',
                                padding: '12px',
                                cursor: 'pointer',
                                textAlign: 'center',
                                position: 'relative',
                              }}
                              whileHover={{ scale: 1.05, y: -2 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => handlePropToggle(prop.id)}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              exit={{ opacity: 0, scale: 0.8 }}
                              transition={{ delay: index * 0.05 }}
                              layout
                            >
                              <div
                                style={{
                                  fontSize: '24px',
                                  marginBottom: '8px',
                                  color: prop.model?.color || COLOR.disabled,
                                }}
                              >
                                <PixelIcon
                                  name={prop.slot}
                                  size={24}
                                  color={prop.model?.color || COLOR.disabled}
                                />
                              </div>
                              <h5 style={{ fontSize: '10px', margin: '0 0 4px 0' }}>{prop.name}</h5>
                              <p
                                style={{
                                  fontSize: '8px',
                                  margin: 0,
                                  color: 'var(--pixel-text-secondary)',
                                }}
                              >
                                {prop.slot}
                              </p>
                              {isEquipped && (
                                <motion.div
                                  style={{
                                    position: 'absolute',
                                    top: '8px',
                                    right: '8px',
                                    background: 'var(--pixel-success)',
                                    borderRadius: '50%',
                                    width: '16px',
                                    height: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                  }}
                                  initial={{ scale: 0 }}
                                  animate={{ scale: 1 }}
                                  transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                                >
                                  <PixelIcon name="success" size={8} color="white" />
                                </motion.div>
                              )}
                            </motion.div>
                          );
                        })
                      )}
                    </AnimatePresence>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </LayoutGroup>
    </Modal>
  );
};

export default CharacterCustomizationModal;
